import { useContext, useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IonIcon, IonToggle } from "@ionic/react";
import { Button, Modal, message } from "antd";
import { closeOutline, ellipsisVertical, lockClosedOutline, globeOutline, downloadOutline, trashOutline, shieldOutline } from "ionicons/icons";

// Assets
import deleteIcon from "../../../assets/delete_icon.svg";
import deleteHoverIcon from "../../../assets/delete_icon_hover.svg";
import downloadIcon from "../../../assets/download_icon.svg";
import downloadHoverIcon from "../../../assets/download_icon_hover.svg";

// Context and Services
import AppContext from "../../../contexts/AppContext";
import {
  deleteFileFromStorage,
  downloadSVGFromStorage,
  // incrementViewOrDownloadCount, // Download count tracking removed - not needed currently
  updateFilePublicStatus,
  normalizeStitchFileId,
} from "../../../lib/firebase/firebase";

// Redux Actions
import { setShowEditor, updateFilePrivacyInRedux as updateMyFilePrivacy } from "../../../redux/slices/MyFiles";
import { deleteUploadedFileInRedux, updateFilePrivacyInRedux as updateUploadedFilePrivacy } from "../../../redux/slices/UploadedFiles";

// Components
import CommunityImageModal from "./CommunityImageModal";
import CanvaPopover from "./CanvaPopover";
import PrivacyPopup from "./PrivacyPopup";
import { useCanvaPopover } from "./useCanvaPopover";
import LazyImage from "./LazyImage";
import { getStitchDifficulty } from "../../../utils/stitchDifficulty";
import { getStitchTime } from "../../../utils/stitchTime";
import { getShortTitle } from "../../../utils/designTitle";

// Styles
import "./styles.css";

const Card = ({ file, index, onImageLoaded }) => {
  // State
  const [deleteModalState, setDeleteModalState] = useState({
    showDeleteAlert: false,
    isDeleting: false,
  });
  const [privacyModalState, setPrivacyModalState] = useState({
    showPrivacyModal: false,
    isUpdating: false,
  });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [communityModalOpen, setCommunityModalOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Refs and Context
  const cardRef = useRef(null);
  const popoverWasOpenRef = useRef(false);
  const privacyModalWasOpenRef = useRef(false);
  const { setSvgStore } = useContext(AppContext);

  // Popover hook
  const popover = useCanvaPopover();
  
  // Track when popover opens/closes to prevent card clicks
  useEffect(() => {
    if (popover.isOpen) {
      popoverWasOpenRef.current = true;
    }
  }, [popover.isOpen]);
  
  // Track viewport changes to update mobile/desktop view
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 768;
      if (newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Redux
  const dispatch = useDispatch();
  const currentTab = useSelector((state) => state.currentTab.tab);

  // Helper function to determine if file should be considered public
  const getFilePublicStatus = useCallback((fileObj) => {
    // All files can now be public/private, regardless of type
    // If isPublic is explicitly set, use that value
    if (fileObj?.isPublic !== undefined) {
      return fileObj.isPublic === true;
    }
    // Default: all files are private by default
    return false;
  }, []);
  
  // Check for invalid file after all hooks are defined
  const isValidFile = file && file.uri;

  // Only the community grid shows these. Elsewhere the user owns the file and
  // already has the real numbers in the editor, so a stale export stat would
  // just contradict what they can see.
  const stats = currentTab === "community" ? file?.stitchStats : null;
  const difficulty = getStitchDifficulty(stats);
  // Time rather than stitch count: the raw number only means something to people
  // who already digitize, and it's the one stat that changes most when the
  // recipient re-exports at their own size anyway.
  const stitchTime = getStitchTime(stats);
  const hasSatin = !!stats?.hasSatin;
  const stitchSummary = [
    stitchTime?.label,
    stats?.num_colors ? `${stats.num_colors} ${stats.num_colors === 1 ? "color" : "colors"}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  // The desktop grid and the mobile row label a card the same way, so this stays
  // in one place rather than being derived twice. Community designs lead with the
  // generated seoTitle; the user's own files lead with the name they know it by.
  const rawTitle = (
    currentTab === "community"
      ? file?.seoTitle || file?.prompt || file?.name
      : file?.filename || file?.name || file?.prompt
  )?.replace(/\.[^/.]+$/, "");
  // Dropped only in the grid. The modal still shows the full generated title,
  // where there's room for it and it reads as information rather than filler.
  const displayTitle = currentTab === "community" ? getShortTitle(rawTitle) : rawTitle;
  
  // Debug logging for file issues
  if (file && !file.url) {
    console.warn("⚠️ Card: File missing URL", { 
      id: file.id, 
      filepath: file.filepath,
      type: file.type,
      hasUri: !!file.uri 
    });
  }

  // Handlers
  const handleRightClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (currentTab !== "community") {
      // Capture mouse position
      setMousePosition({
        x: e.clientX,
        y: e.clientY
      });
      popover.open();
    }
  }, [currentTab, popover]);

  const handleDotsClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (currentTab !== "community") {
      // For dots click, use button position instead of mouse position
      setMousePosition(null);
      popover.toggle();
    }
  }, [currentTab, popover]);

  const handleDeleteFile = useCallback(async () => {
    if (!file?.uri) return;
    try {
      setDeleteModalState((prev) => ({ ...prev, isDeleting: true }));
      await deleteFileFromStorage(file.uri);
      dispatch(deleteUploadedFileInRedux({ id: file.id }));
    } catch (error) {
      console.error("Error deleting file:", error);
    } finally {
      setDeleteModalState((prev) => ({
        ...prev,
        isDeleting: false,
        showDeleteAlert: false,
      }));
    }
  }, [dispatch, file]);

  const handleDownload = useCallback(async () => {
    if (!file?.uri) return;
    try {
      const url = await downloadSVGFromStorage(file.uri);
      const svgString = await (await fetch(url)).text();

      const blob = new Blob([svgString], { type: "image/svg+xml" });
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = file.name || "download.svg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);

      // Download count tracking removed - not needed currently
      // await incrementViewOrDownloadCount(file.id, "download");
    } catch (error) {
      console.error("Error downloading SVG:", error);
    }
  }, [file]);

  const handlePrivacyChange = useCallback(async (newIsPublic) => {
    if (!file?.uri) return;
    try {
      setPrivacyModalState((prev) => ({ ...prev, isUpdating: true }));
      await updateFilePublicStatus(file.uri, newIsPublic);
      
      // Update Redux state to reflect the change in both slices
      dispatch(updateMyFilePrivacy({ id: file.id, isPublic: newIsPublic }));
      dispatch(updateUploadedFilePrivacy({ id: file.id, isPublic: newIsPublic }));
      
      // Set flag to prevent card click from opening editor
      privacyModalWasOpenRef.current = true;
      
      setPrivacyModalState({
        showPrivacyModal: false,
        isUpdating: false,
      });
    } catch (error) {
      console.error("Error updating privacy status:", error);
      setPrivacyModalState((prev) => ({ ...prev, isUpdating: false }));
    }
  }, [file, dispatch]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    onImageLoaded?.();
  }, [onImageLoaded]);

  const handleEditorOpen = useCallback(() => {
    if (!file?.uri) return;
    // If a stitch conversion was in progress when the user last left this file,
    // re-open directly in Stitch mode so the auto-restart can fire.
    let workflow = "vector";
    try {
      const raw = localStorage.getItem(`pendingConversion:${normalizeStitchFileId(file.id)}`);
      if (raw) {
        const { startedAt } = JSON.parse(raw);
        if (Date.now() - startedAt < 2 * 60 * 60 * 1000) workflow = "stitch";
      }
    } catch (_) {}
    dispatch(setShowEditor({ showEditor: true, currentFile: { ...file, workflow } }));
    setSvgStore({ firebase: file.uri });
  }, [dispatch, setSvgStore, file]);

  // Popover actions
  const getPopoverActions = useCallback(() => {
    const actions = [];

    if (currentTab !== "community") {
      actions.push({
        type: 'download',
        icon: downloadOutline,
        label: 'Download',
        onClick: handleDownload,
      });

      const isCurrentlyPublic = getFilePublicStatus(file);
      actions.push({
        type: 'privacy',
        icon: isCurrentlyPublic ? globeOutline : lockClosedOutline,
        label: 'Privacy',
        onClick: () => setPrivacyModalState((prev) => ({ ...prev, showPrivacyModal: true })),
      });

      actions.push({
        type: 'delete',
        icon: trashOutline,
        label: 'Delete',
        onClick: () => setDeleteModalState((prev) => ({ ...prev, showDeleteAlert: true })),
      });
    }

    return actions;
  }, [currentTab, handleDownload, getFilePublicStatus, file]);

  // Early return if file is invalid - must be after all hooks
  if (!isValidFile) {
    console.warn("Card: Invalid file object or missing uri", { file, index });
    return null;
  }

  return (
    <div 
      className="custom-card"
      ref={cardRef} 
      onContextMenu={handleRightClick}
      onClick={(e) => {
        // Don't handle card click if modal is already open
        if (communityModalOpen) return;
        
        // Don't handle card click if privacy modal is open or was recently closed
        if (privacyModalState.showPrivacyModal) return;
        if (privacyModalWasOpenRef.current) {
          privacyModalWasOpenRef.current = false;
          return;
        }
        
        // Don't handle card click if popover was recently open
        // This prevents editor from opening when clicking popover actions
        if (popoverWasOpenRef.current) {
          popoverWasOpenRef.current = false;
          return;
        }
        
        if (currentTab !== "community") {
          handleEditorOpen();
        } else {
          setCommunityModalOpen(true);
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      <div className="nft__item" style={{ height: "100%", margin: 0, display: "flex", flexDirection: "column" }}>
        {/* Public indicator - only show for public files */}
        {currentTab !== "community" && getFilePublicStatus(file) && (
          <div className="flex-start">
            <div
              className="public-private-indicator"
              title="Public — anyone can find this in the community"
            >
              <IonIcon
                size="small"
                icon={globeOutline}
                className="public-icon"
              />
            </div>
          </div>
        )}
        
        {/* Desktop 3-dots menu */}
        {currentTab !== "community" && !isMobile && (
          <div className="flex-end">
            <div 
              className="icon-wrapper" 
              ref={popover.triggerRef}
              onClick={(e) => {
                e.stopPropagation();
                handleDotsClick(e);
              }}
            >
              <IonIcon
                size="small"
                cursor="pointer"
                icon={ellipsisVertical}
                slot="end"
              />
            </div>
          </div>
        )}

        <div className="nft__item_wrap" style={{ flex: 1, minHeight: 0, position: "relative" }}>
          <div className="image_div">
            <LazyImage
              src={file.url}
              alt={file.name}
              onLoad={handleImageLoad}
              onError={(e) => console.error("❌ Image failed to load:", file.url, e)}
              rootMargin="300px" // Start loading 300px before entering viewport
            />
          </div>
          {/* Sits on the artwork so it survives scanning the grid. Only public
              embroidery designs carry stitchStats, so this never renders on
              SvgTrace or on a design published without an export. */}
          {difficulty && (
            <div
              className="card-difficulty-badge"
              style={{ color: difficulty.color, background: difficulty.background }}
              title={`${difficulty.label} — ${difficulty.detail}`}
            >
              {difficulty.label}
            </div>
          )}
        </div>

        {/* Text content container - Desktop/Grid View */}
        <div className="desktop-card-text" style={{ flexShrink: 0, padding: "2px 4px" }}>
          {currentTab !== "community" && file.prompt && (
            <div style={{ textAlign: "center", fontSize: "10px", color: "#444", marginTop: "1px", wordBreak: "break-word", padding: "0 2px", lineHeight: "1.2", maxHeight: "24px", overflow: "hidden" }}>
              {file.prompt}
            </div>
          )}
          {currentTab === "community" && displayTitle && (
            <div className="card-title" title={displayTitle}>
              {displayTitle}
            </div>
          )}
          {(stitchSummary || hasSatin) && (
            <div className="card-stitch-summary">
              {stitchSummary && (
                <span title={stitchTime ? stitchTime.detail : "From the creator's export — re-exporting at your own size changes these"}>
                  {stitchSummary}
                </span>
              )}
              {hasSatin && (
                <span className="card-satin-marker" title="Uses satin stitching — raised, glossy borders and lettering">
                  Satin
                </span>
              )}
            </div>
          )}
        </div>

        {/* Mobile List View - Info Container */}
        <div className="mobile-card-info">
          <div className="mobile-card-title">{displayTitle || "Untitled"}</div>
          {(stitchSummary || hasSatin) && (
            <div className="mobile-card-stitch-summary">
              {difficulty && (
                <span style={{ color: difficulty.color, fontWeight: 700 }}>
                  {difficulty.label}
                </span>
              )}
              {difficulty && stitchSummary ? " · " : null}
              {stitchSummary}
              {hasSatin && <span className="card-satin-marker">Satin</span>}
            </div>
          )}
        </div>

        {/* Mobile Action Buttons - Inline on the right */}
        {isMobile && currentTab !== "community" && (
          <div 
            className="mobile-card-actions"
            onClick={(e) => e.stopPropagation()}
          >
            {getPopoverActions().map((action, index) => (
              <button
                key={index}
                className="mobile-action-button"
                onClick={(e) => {
                  e.stopPropagation();
                  // Set flag to prevent card click from opening editor
                  popoverWasOpenRef.current = true;
                  action.onClick();
                }}
                title={action.label}
              >
                <IonIcon icon={action.icon} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Canva Popover - Only show on desktop, outside card container to avoid z-index issues */}
      {!isMobile && (
        <CanvaPopover
          isOpen={popover.isOpen}
          onClose={popover.close}
          triggerRef={popover.triggerRef}
          mousePosition={mousePosition}
          actions={getPopoverActions()}
          placement="auto"
        />
      )}

      {/* Modals - Outside flip container */}
      <PrivacyPopup
        isOpen={privacyModalState.showPrivacyModal}
        onClose={() => {
          privacyModalWasOpenRef.current = true;
          setPrivacyModalState((prev) => ({ ...prev, showPrivacyModal: false }));
        }}
        file={file}
        onPrivacyChange={handlePrivacyChange}
        isUpdating={privacyModalState.isUpdating}
      />

      <Modal
        open={deleteModalState.showDeleteAlert}
        centered
        onCancel={() =>
          setDeleteModalState((prev) => ({ ...prev, showDeleteAlert: false }))
        }
        footer={null}
        closable={false}
      >
        <div className="custom-modal-container">
          <h2 className="custom-modal-title">Are you sure?</h2>
          <p className="custom-modal-message">
            This action cannot be undone.
          </p>
          <div className="custom-modal-buttons">
            <button
              className="custom-modal-button"
              onClick={() =>
                setDeleteModalState((prev) => ({
                  ...prev,
                  showDeleteAlert: false,
                }))
              }
            >
              Cancel
            </button>
            <button
              className="custom-modal-button"
              onClick={handleDeleteFile}
              disabled={deleteModalState.isDeleting}
            >
              {deleteModalState.isDeleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Community Image Modal */}
      {currentTab === "community" && (
        <CommunityImageModal
          isOpen={communityModalOpen}
          onClose={() => setCommunityModalOpen(false)}
          file={file}
        />
      )}
    </div>
  );
};

export default Card;
